const {
  formatMoney,
  isANumber,
  parseInteger,
  buildProperties,
} = window.BAO.Utils

class ProductDetailSetsMobile extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['variant', 'subtotal', 'button', 'wishlist']
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.handleFormSubmission = this.handleFormSubmission.bind(this)
    this.onQuantityUpdate = this.onQuantityUpdate.bind(this)
    this.onMainFormStateChange = this.onMainFormStateChange.bind(this)
    this.setupMainFormObserver = this.setupMainFormObserver.bind(this)

    if (this.els?.button?.exists && this.els?.form?.exists) {
      this.listeners.add(
        this.els.button.element,
        'click',
        this.handleFormSubmission,
      )
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.onQuantityUpdate,
    )

    // Ensures the total is correct on page load
    requestAnimationFrame(() => {
      this.updateTotals()
      this.setupMainFormObserver()
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.mainFormObserver?.disconnect()
  }

  setupMainFormObserver () {
    this.mainFormObserver?.disconnect()

    const mainFormEl = this.mainProductFormEl
    if (!mainFormEl) return

    this.mainFormObserver = new MutationObserver(this.onMainFormStateChange)

    this.mainFormObserver.observe(mainFormEl, {
      attributes: true,
      attributeFilter: ['style', 'data-product-available'],
    })
  }

  onMainFormStateChange () {
    this.applyMainFormInlineHiddenGuard()
    this.updateButtonStates()
  }

  /**
   * @returns {boolean}
   */
  isBaseProductSoldOut () {
    const mainFormEl = this.mainProductFormEl

    return mainFormEl?.getAttribute('data-product-available') !== 'true'
  }

  /**
   * The rows a customer can actually buy. The base product's row ships with
   * data-quantity="1" even when it is sold out (the adjuster only clamps it
   * to 0 later, and only when the variant carries a max), so totals, the
   * sold-out check and the cart payload all skip unavailable rows (#448).
   *
   * @returns {HTMLElement[]}
   */
  purchasableVariantEls () {
    return this.els.variant.elements.filter(variantEl => {
      return !variantEl.classList.contains('prd-DetailForm_Variant-unavailable')
    })
  }

  /**
   * Mirrors product-detail-sets.js on desktop (#448): a sold-out base with
   * nothing selected shows the sold-out message and the wishlist button;
   * selecting a sibling swaps in Add to Bag. Only the sets footer opts in,
   * by rendering the `message` element; product-detail-bundle-mobile mounts
   * this same element without it and keeps its Liquid-decided footer.
   *
   * @returns {void}
   */
  updateButtonStates () {
    const messageEl = this.els?.message?.element
    const buttonEl = this.els?.button?.element
    const wishlistEl = this.els?.wishlist?.element
    if (!messageEl || !buttonEl || !wishlistEl) return

    const showWishlist = this.isBaseProductSoldOut() && this.subtotal < 1

    buttonEl.setAttribute('aria-hidden', String(showWishlist))
    wishlistEl.setAttribute('aria-hidden', String(!showWishlist))
    messageEl.setAttribute('aria-hidden', String(!showWishlist))
  }

  applyMainFormInlineHiddenGuard () {
    const buttonEl = this.els?.button?.element
    if (!buttonEl) return false

    const shouldHide = this.isMainProductFormInlineHidden()
    buttonEl.style.display = shouldHide ? 'none' : ''
    buttonEl.disabled = shouldHide

    return shouldHide
  }

  isMainProductFormInlineHidden () {
    const mainFormEl = this.mainProductFormEl
    if (!mainFormEl) return false

    return mainFormEl.style?.display === 'none'
  }

  get mainProductFormEl () {
    return (
      this.externalEls?.form?.element ||
      document.querySelector('.prd-DetailForm_Form[data-product-form]')
    )
  }

  /**
   * @param {Event} event
   *
   * @returns {void}
   */
  onQuantityUpdate (event) {
    const variantEl = event.target.closest(this.els?.variant?.selector)
    if (!isANumber(event?.detail?.value) || !variantEl) return

    variantEl.setAttribute('data-quantity', String(event.detail.value))

    this.updateTotals()
  }

  /**
   * @returns {void}
   */
  handleFormSubmission () {
    if (this.applyMainFormInlineHiddenGuard()) {
      return
    }

    this.calculateTotalQuantity()

    const isValid = this.totalQuantity > 0
    this.els.button.element.setAttribute('aria-invalid', String(!isValid))

    if (!isValid) return

    const itemsArray = this.purchasableVariantEls()
      .map(variantEl => variantEl.querySelector('form'))
      .filter(formEl => formEl)
      .map(formEl => {
        const formData = new FormData(formEl)
        const id = parseInteger(formData.get('id'))
        const quantity = parseInteger(formData.get('quantity'))
        if (!id || !quantity) return null
        return {
          id,
          quantity,
          properties: buildProperties(formData),
        }
      })
      .filter(item => item)

    window.BAO.Cart.addItem(itemsArray)
  }

  calculateTotalQuantity () {
    this.totalQuantity = this.purchasableVariantEls()
      .map(variantEl => {
        return parseInteger(variantEl?.dataset?.quantity) || 0
      })
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue
      }, 0)
  }

  calculateSubtotal () {
    this.subtotal = this.purchasableVariantEls()
      .map(variantEl => {
        return (
          (parseInteger(variantEl?.dataset?.price) || 0) *
          (parseInteger(variantEl?.dataset?.quantity) || 0)
        )
      })
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue
      }, 0)
  }

  updateTotals () {
    this.els.button.element.setAttribute('aria-invalid', 'false')
    this.calculateTotalQuantity()
    this.calculateSubtotal()
    this.applyMainFormInlineHiddenGuard()
    this.updateButtonStates()
  }

  /**
   * @returns {number} quantity - The current running total quantity selected with the associated quantity-adjusters
   */
  get totalQuantity () {
    return this._totalQuantity || 0
  }

  /**
   * @param {number} quantity - The current running total quantity selected with the associated quantity-adjusters
   *
   * @returns {undefined}
   */
  set totalQuantity (quantity) {
    this._totalQuantity = quantity
  }

  /**
   * @returns {number} subtotal - The current running subotal
   */
  get subtotal () {
    return this._subtotal || 0
  }

  /**
   * @param {number} subtotal - The current running subotal
   *
   * @returns {undefined}
   */
  set subtotal (subtotal) {
    this._subtotal = subtotal
    this.els.subtotal.element.textContent = `(${formatMoney(subtotal)})`
    this.els.subtotal.element.setAttribute('aria-hidden', String(subtotal < 1))
  }
}

if (!customElements.get('product-detail-sets-mobile')) {
  customElements.define('product-detail-sets-mobile', ProductDetailSetsMobile)
}
